import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { ALL_FAMILIES, type FamilyKey } from "@/data/activation-families";
import { readStoredFamilies, writeStoredFamilies } from "@/lib/stored-families";

/** Families this brand may use. Admins see all four. Column is set per brand from admin. */
export function useActivationAccess() {
  const { session, loading: authLoading } = useAuth();
  const { data: isAdmin, isPending: adminPending } = useIsAdmin();
  const userId = session?.user.id;

  // Keyed by user and started right away. It no longer waits for the brand profile to load first,
  // which made the sidebar wait on three requests in a row.
  const { data, isPending } = useQuery({
    queryKey: ["activation-families", userId],
    enabled: !authLoading && !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<FamilyKey[]> => {
      const { data: member } = await supabase.from("team_members").select("brand_id").eq("user_id", userId!).limit(1).maybeSingle();
      if (!member?.brand_id) return [];
      // activation_families is newer than the generated types, so go untyped here.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: brand, error } = await (supabase as any)
        .from("brands")
        .select("activation_families")
        .eq("id", member.brand_id)
        .maybeSingle();
      if (error) {
        // Column not there yet or lookup failed: every brand defaults to all four families.
        console.warn("activation_families lookup failed, defaulting to all families", error);
        return ALL_FAMILIES;
      }
      const raw = (brand?.activation_families ?? ALL_FAMILIES) as string[];
      return ALL_FAMILIES.filter((f) => raw.includes(f));
    },
  });

  // What this brand could use last time. It lets the sidebar show the right links on the first paint.
  const stored = useMemo(() => readStoredFamilies(userId), [userId]);
  useEffect(() => {
    if (data) writeStoredFamilies(userId, data);
  }, [data, userId]);

  // Not settled until we know who the user is and what that brand may use.
  const isLoading = authLoading || adminPending || (!!userId && isPending);
  const families: FamilyKey[] = isAdmin ? ALL_FAMILIES : data ?? [];
  // For the sidebar only: while it loads, show the last known links, or all four on a first visit, so the
  // menu does not fill in piece by piece. Links to a stream a brand cannot use still lead to its notice.
  const navFamilies: FamilyKey[] = isAdmin ? ALL_FAMILIES : data ?? stored ?? ALL_FAMILIES;
  return { families, navFamilies, canUse: (f: FamilyKey) => families.includes(f), isLoading };
}
